import type {
  NoteDiagnosis,
  TpPlanCard,
  TpPlanList,
  TpPlanStatus,
  TpPlanSummary,
} from '@ehr/shared';
import { clockTime } from './timePrefs.js';

/**
 * ehr#76 / ehr#256 - the client drawer's Treatment Plans tab (TREATMENT_PLANS_MODULE_10-05_V3:
 * a table of plans with a details panel per row): pure logic, no DOM and no clock (today is
 * passed in). Lateness is never stored; it is derived from the due dates on every render.
 */

export const TP_STATUS_LABEL: Record<TpPlanStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  received: 'Received',
  signed: 'Signed',
  revision: 'Needs revision',
};

/** The issue's rule: a next review within this many days shows orange, past due red. */
export const REVIEW_SOON_DAYS = 10;

const MS_PER_DAY = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A calendar day, parsed at noon UTC so no timezone offset can roll it backwards. */
function parseDay(iso: string | null | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
  return m ? new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, 12)) : null;
}

export function daysBetween(fromIso: string | null, toIso: string): number | null {
  const a = parseDay(fromIso);
  const b = parseDay(toIso);
  return a && b ? Math.round((b.getTime() - a.getTime()) / MS_PER_DAY) : null;
}

/** "Sep 9, 2026". Timestamps use the local day; plain dates their own. */
export function fmtDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const local = /T/.test(iso) ? new Date(iso) : null;
  if (local && !isNaN(local.getTime()))
    return `${MONTHS[local.getMonth()]} ${local.getDate()}, ${local.getFullYear()}`;
  const d = parseDay(iso);
  return d ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}` : '';
}

// ehr#98: "6:30 PM" or "18:30", per the person's time format.
const clock = (d: Date): string => clockTime(d);

/** "Sep 9, 2026 · 2:20 PM" (details panel). */
export function fmtStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return !/T/.test(iso) || isNaN(d.getTime()) ? fmtDay(iso) : `${fmtDay(iso)} · ${clock(d)}`;
}

/** "Sep 9" (the track). */
export function fmtShort(iso: string | null | undefined): string {
  return fmtDay(iso).replace(/, \d{4}$/, '');
}

/** "09/01/2026" (the table's date column; a timestamp reads as its local day). */
export function fmtSlash(iso: string | null | undefined): string {
  if (!iso) return '';
  const local = /T/.test(iso) ? new Date(iso) : null;
  const p = (n: number) => String(n).padStart(2, '0');
  if (local && !isNaN(local.getTime()))
    return `${p(local.getMonth() + 1)}/${p(local.getDate())}/${local.getFullYear()}`;
  const d = parseDay(iso);
  return d ? `${p(d.getUTCMonth() + 1)}/${p(d.getUTCDate())}/${d.getUTCFullYear()}` : '';
}

export const pluralDays = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
/** "in 0 days" is not a sentence anyone says. */
export const dueInPhrase = (n: number) => (n === 0 ? 'due today' : `in ${pluralDays(n)}`);

export interface PlanFlags {
  isCurrent: boolean;
  needsRevision: boolean;
  awaitingSignature: boolean;
  signatureLateDays: number;
  reviewLateDays: number;
  /** Days until the next review (0 = today); null when none or already late. */
  reviewDueInDays: number | null;
  /** Within REVIEW_SOON_DAYS of the next review (orange). */
  reviewSoon: boolean;
  isLate: boolean;
  needsAttention: boolean;
}

export function computePlanFlags(plan: TpPlanCard, today: string): PlanFlags {
  const awaitingSignature =
    (plan.status === 'submitted' || plan.status === 'received') && !plan.signedAt;
  let signatureLateDays = 0;
  let reviewLateDays = 0;
  let reviewDueInDays: number | null = null;
  // Late, kind one: waiting on a co-signature past its deadline.
  if (awaitingSignature && plan.signatureDue) {
    const over = daysBetween(plan.signatureDue, today);
    if (over !== null && over > 0) signatureLateDays = over;
  }
  // Late, kind two: the review date has passed. Only a live plan is reviewed. Any status
  // counts, as on TP's own Saved-plans list: a draft past its review date is overdue too.
  if (plan.nextReviewDue && plan.isCurrent) {
    const over = daysBetween(plan.nextReviewDue, today);
    if (over !== null) {
      if (over > 0) reviewLateDays = over;
      else reviewDueInDays = Math.max(0, -over);
    }
  }
  const needsRevision = plan.status === 'revision';
  const isLate = signatureLateDays > 0 || reviewLateDays > 0;
  return {
    isCurrent: plan.isCurrent,
    needsRevision,
    awaitingSignature,
    signatureLateDays,
    reviewLateDays,
    reviewDueInDays,
    reviewSoon: reviewDueInDays !== null && reviewDueInDays <= REVIEW_SOON_DAYS,
    isLate,
    needsAttention: isLate || needsRevision,
  };
}

/** Newest first, but the current plan always leads: it is the one being worked. */
export function sortPlans(plans: TpPlanCard[]): TpPlanCard[] {
  return plans.slice().sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    return String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''));
  });
}

export interface PlanRow {
  plan: TpPlanCard;
  flags: PlanFlags;
}
export function summarizePlans(plans: TpPlanCard[], today: string) {
  const rows: PlanRow[] = sortPlans(plans).map((plan) => ({
    plan,
    flags: computePlanFlags(plan, today),
  }));
  return {
    rows,
    total: rows.length,
    current: rows.find((r) => r.flags.isCurrent) ?? null,
    attention: rows.filter((r) => r.flags.needsAttention).length,
  };
}

export type PlanTone = 'revision' | 'late' | 'open' | 'good' | 'soon' | 'past';
export interface PlanDescription {
  tone: PlanTone;
  headline: string;
  subline: string;
  action: string;
}

/** One sentence saying what is true, one saying why, and the verb for the button. */
export function describePlan(plan: TpPlanCard, f: PlanFlags): PlanDescription {
  if (f.needsRevision)
    return {
      tone: 'revision',
      headline: 'Sent back for revision',
      subline:
        plan.revisionReason ||
        `Returned ${fmtDay(plan.revisionRequestedAt) || 'by the supervisor'} without a co-signature.`,
      action: 'Revise the plan',
    };
  if (f.signatureLateDays > 0)
    return {
      tone: 'late',
      headline: `Co-signature is ${pluralDays(f.signatureLateDays)} overdue`,
      subline: `Submitted ${fmtDay(plan.receivedAt || plan.submittedAt)}. Was due for co-signature ${fmtDay(plan.signatureDue)}.`,
      action: 'Co-sign the plan',
    };
  if (f.reviewLateDays > 0) {
    const due = `Was due for review ${fmtDay(plan.nextReviewDue)}.`;
    if (plan.status === 'signed')
      return {
        tone: 'late',
        headline: `Review is ${pluralDays(f.reviewLateDays)} overdue`,
        subline: `Signed ${fmtDay(plan.signedAt)}. ${due}`,
        action: 'Start the review',
      };
    // Not signed yet and already past its review date: the overdue review leads, and
    // the button still names what the plan needs next.
    return {
      tone: 'late',
      headline: `Review is ${pluralDays(f.reviewLateDays)} overdue`,
      subline: f.awaitingSignature
        ? `Waiting on a co-signature since ${fmtDay(plan.submittedAt)}. ${due}`
        : `Draft, not signed yet; last edited ${fmtDay(plan.updatedAt)}. ${due}`,
      action: f.awaitingSignature ? 'Co-sign the plan' : 'Finish the draft',
    };
  }
  if (f.awaitingSignature)
    return {
      tone: 'open',
      headline: 'Waiting on a co-signature',
      subline: `${plan.receivedAt ? `Received ${fmtDay(plan.receivedAt)}` : `Submitted ${fmtDay(plan.submittedAt)}`}. ${plan.signatureDue ? `Due ${fmtDay(plan.signatureDue)}.` : 'No deadline set.'}`,
      action: 'Co-sign the plan',
    };
  if (plan.status === 'draft')
    return {
      tone: 'open',
      headline: 'Draft, not submitted yet',
      subline: `Started ${fmtDay(plan.createdAt)}, last edited ${fmtDay(plan.updatedAt)}.`,
      action: 'Finish the draft',
    };
  if (plan.status === 'signed' && f.reviewDueInDays !== null)
    return {
      tone: f.reviewSoon ? 'soon' : 'good',
      headline:
        f.reviewDueInDays === 0
          ? 'Review is due today'
          : f.reviewSoon
            ? `Review is due ${dueInPhrase(f.reviewDueInDays)}`
            : 'Signed and on schedule',
      subline: `Signed ${fmtDay(plan.signedAt)}. Next review ${fmtDay(plan.nextReviewDue)}, ${dueInPhrase(f.reviewDueInDays)}.`,
      action: f.reviewSoon ? 'Start the review' : 'Open plan',
    };
  if (plan.isCurrent)
    return {
      tone: 'good',
      headline: `Signed ${fmtDay(plan.signedAt)}`,
      subline: 'No review date set. Add one in the plan.',
      action: 'Open plan',
    };
  // A past plan: an earlier episode of care.
  return {
    tone: 'past',
    headline: plan.signedAt ? `Signed ${fmtDay(plan.signedAt)}` : 'Not signed',
    subline: 'Prior episode of care. No further review scheduled.',
    action: 'Open plan',
  };
}

/** ehr#256 - the Mode column: Initial / Standard / Discharge. */
export type PlanMode = 'Initial' | 'Standard' | 'Discharge';
const PLAN_TYPE_MODE: Record<string, PlanMode> = {
  initial: 'Initial',
  standard: 'Standard',
  discharge: 'Discharge',
};

/**
 * The TP app records no plan type yet (Jay, 2026-10-08: derive it): the client's first plan is
 * Initial and every later one Standard. A planType the TP app sends wins once it sends one.
 */
export function planMode(plan: TpPlanCard, all: TpPlanCard[]): PlanMode {
  const sent = plan.planType ? PLAN_TYPE_MODE[plan.planType] : undefined;
  if (sent) return sent;
  const first = all.reduce<TpPlanCard | null>(
    (a, p) => (!a || String(p.createdAt ?? '') < String(a.createdAt ?? '') ? p : a),
    null,
  );
  return first && first.id === plan.id ? 'Initial' : 'Standard';
}

export type PillTone = 'revision' | 'late' | 'open' | 'draft' | 'good';

/** The status pill. Late beats everything, because late is what someone has to act on. */
export function planStatusPill(plan: TpPlanCard, f: PlanFlags): { label: string; tone: PillTone } {
  if (f.needsRevision) return { label: 'Needs revision', tone: 'revision' };
  if (f.signatureLateDays > 0) return { label: 'Co-sign late', tone: 'late' };
  if (f.reviewLateDays > 0) return { label: 'Review overdue', tone: 'late' };
  if (f.awaitingSignature) return { label: 'Awaiting co-sign', tone: 'open' };
  if (plan.status === 'draft') return { label: 'Draft', tone: 'draft' };
  return { label: 'Co-signed', tone: 'good' };
}

/** Why a plan needs attention, read at the top of its details panel. */
export function planReasons(plan: TpPlanCard, f: PlanFlags): string[] {
  const out: string[] = [];
  if (f.signatureLateDays > 0)
    out.push(
      `Co-signature is ${pluralDays(f.signatureLateDays)} past the ${fmtDay(plan.signatureDue)} deadline.`,
    );
  if (f.reviewLateDays > 0)
    out.push(
      plan.reviewCadenceDays
        ? `Review is ${pluralDays(f.reviewLateDays)} overdue on a ${plan.reviewCadenceDays} day cadence.`
        : `Review is ${pluralDays(f.reviewLateDays)} overdue.`,
    );
  if (f.needsRevision) out.push(plan.revisionReason || 'Sent back for revision.');
  return out;
}

/** Today as the local calendar day (YYYY-MM-DD). */
export function localToday(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** The TP link a plan card's button opens. "Start the review" adds review=1, which the
 *  TP app reads to open the plan straight into its Plan review window (tp#12). */
export function tpPlanUrl(tpLink: string, action: string): string {
  if (action !== 'Start the review') return tpLink;
  return `${tpLink}${tpLink.includes('?') ? '&' : '?'}review=1`;
}

/** "+ Add new": the TP app opens on a new plan for this client (its new=1 parameter). */
export function tpNewPlanUrl(tpLink: string): string {
  return `${tpLink}${tpLink.includes('?') ? '&' : '?'}new=1`;
}

/** The treatment-plans module (tp-credify). */
export const TP_BASE = 'https://tp.credifyfast.com';

/**
 * tp#299 (Jay 2026-11-03): "A provider can NEVER start a treatment plan without navigating to an
 * Active client chart." The only way into the TP app is this link, made by an ACTIVE client's
 * chart: a client (not a lead, staff or referral source) who is not archived. Anything else gets
 * null, and the chart offers no + Add new, no Open in Treatment Plans and no plan row that opens
 * the TP. The TP app refuses the same launch on its own server (tp-credify launch guard).
 */
export function tpChartLink(contact: {
  id: string;
  category?: string | null;
  archivedAt?: string | null;
}): string | null {
  if (!contact.id || contact.category !== 'client' || contact.archivedAt) return null;
  return `${TP_BASE}/?contact=${encodeURIComponent(contact.id)}`;
}

// ── ehr#526: Treatment Plan Builder plans in the chart and on progress notes ──────────────────
// Plans are built and signed in the TP app (tp.credifyfast.com). The chart's Treatment plans list
// and a progress note's "Linked plan" read them (GET /contacts/:id/tp-plans and /tp-summary) next
// to any plan written in the EHR itself. TP being down never blocks either: its plans just don't show.

/** A row of a progress note's "Goals addressed", as the note saves it (goalsAddressed). */
export interface PlanGoalRow {
  goalText: string;
  objectiveText: string;
  addressed: boolean;
  progress: string;
  /** ehr#571: the plan's own status for the objective, when the plan keeps one. */
  planStatus?: string;
}

/** The note body keeps at most this many goal rows (progressBodySchema.goalsAddressed). */
const MAX_GOAL_ROWS = 50;

/** The live TP plan's goals as note rows: one per objective; a goal with none keeps one row. */
export function tpSummaryGoals(s: TpPlanSummary | null | undefined): PlanGoalRow[] {
  if (!s?.available) return [];
  const row = (goalText: string, objectiveText: string): PlanGoalRow => ({
    goalText,
    objectiveText,
    addressed: false,
    progress: 'No change',
  });
  const out: PlanGoalRow[] = [];
  for (const g of s.goals ?? []) {
    const objs = (g.objectives ?? []).filter((o) => o.statement);
    if (!objs.length) out.push(row(g.statement, ''));
    for (const o of objs) out.push(row(g.statement, o.statement));
  }
  return out.slice(0, MAX_GOAL_ROWS);
}

/**
 * ehr#682: the live TP plan's problems, for Problems, goals, and objectives addressed. The TP
 * app's summary has no problems yet (goals and objectives only): then there are none. Read
 * loosely so a summary that starts sending them (strings, or { statement } / { problem }) works.
 */
export function tpSummaryProblems(s: TpPlanSummary | null | undefined): string[] {
  if (!s?.available) return [];
  const raw = (s as { problems?: unknown }).problems;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p: unknown) =>
      typeof p === 'string'
        ? p
        : p && typeof p === 'object'
          ? String(
              (p as { statement?: unknown; problem?: unknown }).statement ??
                (p as { problem?: unknown }).problem ??
                '',
            )
          : '',
    )
    .map((p) => p.trim())
    .filter(Boolean);
}

// ── ehr#906 / ehr#907: the linked plan's interventions and diagnoses on a new note ─────────────
/** One of the plan's interventions, with the goal it serves (its words, as the plan has them). */
export interface PlanIntervention {
  text: string;
  goal: string;
}

/** A note's Interventions used item is at most this long (progressBodySchema.interventionsUsed). */
export const INTERVENTION_TEXT_MAX = 120;

/** An intervention's words, cut to fit the note (a long description ends in "..."). */
export function clipIntervention(raw: string): string {
  const t = raw.replace(/\s+/g, ' ').trim();
  return t.length > INTERVENTION_TEXT_MAX
    ? `${t.slice(0, INTERVENTION_TEXT_MAX - 3).trimEnd()}...`
    : t;
}

/** An intervention as the TP summary may send it: words, or { name } / { description } / { statement }. */
function interventionWords(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (!raw || typeof raw !== 'object') return '';
  const r = raw as { name?: unknown; description?: unknown; statement?: unknown };
  const v = [r.name, r.description, r.statement].find((x) => typeof x === 'string' && x.trim());
  return typeof v === 'string' ? v : '';
}

/**
 * ehr#906: the live TP plan's interventions, each with its goal, in plan order. The TP app's
 * summary sends none today (goals and objectives only): then there are none. Read loosely so a
 * summary that starts sending them (on an objective, or on the goal) works.
 */
export function tpSummaryInterventions(s: TpPlanSummary | null | undefined): PlanIntervention[] {
  if (!s?.available) return [];
  const out: PlanIntervention[] = [];
  const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
  for (const g of s.goals ?? []) {
    const goal = typeof g?.statement === 'string' ? g.statement.trim() : '';
    const add = (raw: unknown) => {
      const text = clipIntervention(interventionWords(raw));
      if (text) out.push({ text, goal });
    };
    list((g as { interventions?: unknown }).interventions).forEach(add);
    for (const o of list(g?.objectives))
      list((o as { interventions?: unknown }).interventions).forEach(add);
  }
  return out;
}

/** One Interventions used item from the plan: its words and the goals (numbered) it serves. */
export interface PlanInterventionItem {
  text: string;
  /** "Goal 1", "Goal 2": the goal's number in the note's Treatment plan goals. */
  goals: number[];
  /** The goals' words, for the tag's tooltip. */
  goalTexts: string[];
}

/**
 * ehr#906: the plan's interventions as checklist items, each once (an intervention under two
 * goals is one item labelled with both). A goal's number is its place in the note's Treatment
 * plan goals (`goalOrder`, the goal statements in order); a goal not there follows them.
 */
export function planInterventionItems(
  list: readonly PlanIntervention[] | undefined,
  goalOrder: readonly string[],
): PlanInterventionItem[] {
  const norm = (t: string) => t.replace(/\s+/g, ' ').trim().toLowerCase();
  const order = goalOrder.map(norm);
  const extra: string[] = [];
  const numberOf = (goal: string): number | null => {
    const g = norm(goal);
    if (!g) return null;
    const at = order.indexOf(g);
    if (at >= 0) return at + 1;
    if (!extra.includes(g)) extra.push(g);
    return order.length + extra.indexOf(g) + 1;
  };
  const out: PlanInterventionItem[] = [];
  for (const it of list ?? []) {
    const text = clipIntervention(it.text);
    if (!text) continue;
    const n = numberOf(it.goal);
    let row = out.find((o) => norm(o.text) === norm(text));
    if (!row) {
      row = { text, goals: [], goalTexts: [] };
      out.push(row);
    }
    if (n !== null && !row.goals.includes(n)) {
      row.goals.push(n);
      row.goalTexts.push(it.goal.trim());
    }
  }
  return out;
}

/**
 * ehr#907: the live TP plan's diagnoses as the note keeps them, the plan's Primary first (then
 * the plan's order), each code once. The note's first diagnosis is its Primary.
 */
export function tpSummaryDiagnoses(s: TpPlanSummary | null | undefined): NoteDiagnosis[] {
  if (!s?.available) return [];
  const seen = new Set<string>();
  const rows: (NoteDiagnosis & { primary: boolean })[] = [];
  for (const d of s.diagnoses ?? []) {
    const code = typeof d?.code === 'string' ? d.code.trim().toUpperCase().slice(0, 12) : '';
    if (!code || seen.has(code)) continue;
    seen.add(code);
    rows.push({
      code,
      label: typeof d.label === 'string' ? d.label.trim().slice(0, 200) : '',
      source: 'tp',
      primary: typeof d.type === 'string' && d.type.trim().toLowerCase() === 'primary',
    });
  }
  const first = rows.findIndex((r) => r.primary);
  const ordered = first > 0 ? [rows[first]!, ...rows.filter((_, i) => i !== first)] : rows;
  return ordered.map((d) => ({ code: d.code, label: d.label, source: d.source }));
}

/** ehr#906: an EHR-written plan's interventions (each objective's, by description), with their goal. */
export function ehrPlanInterventions(
  body:
    | {
        goals?: readonly {
          goal_text?: string;
          objectives?: readonly { interventions?: readonly { description?: string }[] }[];
        }[];
      }
    | null
    | undefined,
): PlanIntervention[] {
  const out: PlanIntervention[] = [];
  for (const g of body?.goals ?? [])
    for (const o of g.objectives ?? [])
      for (const i of o.interventions ?? []) {
        const text = clipIntervention(i.description ?? '');
        if (text) out.push({ text, goal: (g.goal_text ?? '').trim() });
      }
  return out;
}

/**
 * ehr#907: a new note takes the linked plan's diagnoses when it has none of its own yet; a
 * diagnosis already on the note (picked, or copied from a note) is never replaced.
 */
export function withPlanDiagnoses(
  current: NoteDiagnosis[],
  plan: { diagnoses?: NoteDiagnosis[] } | null | undefined,
): NoteDiagnosis[] {
  return current.length || !plan?.diagnoses?.length ? current : plan.diagnoses;
}

const TP_STAND: Record<TpPlanStatus, string> = {
  draft: 'draft',
  submitted: 'awaiting co-sign',
  received: 'awaiting co-sign',
  signed: 'signed',
  revision: 'needs revision',
};

/** "Standard treatment plan · Anxiety (signed 10/20/2026)": the plan's title and where it stands. */
export function tpPlanLabel(p: TpPlanCard): string {
  const stand =
    p.status === 'signed' && p.signedAt
      ? `signed ${fmtSlash(p.signedAt)}`
      : p.onHold && !p.isCurrent
        ? 'on hold'
        : (TP_STAND[p.status] ?? p.status);
  return `${p.title || 'Treatment plan'} (${stand})`;
}

/** One choice in a progress note's "Linked plan", with the goals linking it brings in. */
export interface LinkablePlan {
  id: string;
  label: string;
  goals: PlanGoalRow[];
  /** ehr#682: the plan's problems (an EHR plan's presenting problem; TP: when its summary has them). */
  problems?: string[];
  /** ehr#906: the plan's interventions, each with its goal (TP: when its summary has them). */
  interventions?: PlanIntervention[];
  /** ehr#907: the plan's diagnoses, its Primary first (TP: the live plan's, from its summary). */
  diagnoses?: NoteDiagnosis[];
  source: 'ehr' | 'tp';
  /** ehr#571: when the plan was signed; no goal status on a note is dated before it. */
  signedAt?: string | null;
  /** ehr#855: the plan is signed (co-signed in the EHR; status signed in the TP app). */
  signed?: boolean;
}

/**
 * The note's "Linked plan" choices: the client's live TP plan first (the TP app summarizes its
 * goals for exactly that plan), then the plans written in the EHR. A plan a saved note already
 * links stays a choice even when it's no longer the live one, so the note still shows it. No plan
 * is listed twice. A new note's default is defaultLinkablePlan's pick (ehr#855: never a draft
 * while a signed plan exists).
 */
export function mergeLinkablePlans(
  ehr: LinkablePlan[],
  tpList: TpPlanList | null | undefined,
  summary: TpPlanSummary | null | undefined,
  keepId?: string | null,
): LinkablePlan[] {
  const live = summary?.available && summary.planId ? summary.planId : null;
  const goalsOf = (id: string) => (id === live ? tpSummaryGoals(summary) : []);
  const problemsOf = (id: string) => (id === live ? tpSummaryProblems(summary) : []);
  const interventionsOf = (id: string) => (id === live ? tpSummaryInterventions(summary) : []);
  const diagnosesOf = (id: string) => (id === live ? tpSummaryDiagnoses(summary) : []);
  const tp: LinkablePlan[] = [];
  for (const c of tpList?.available ? sortPlans(tpList.plans) : [])
    if (c.isCurrent || c.id === keepId || c.id === live)
      tp.push({
        id: c.id,
        label: tpPlanLabel(c),
        goals: goalsOf(c.id),
        problems: problemsOf(c.id),
        interventions: interventionsOf(c.id),
        diagnoses: diagnosesOf(c.id),
        source: 'tp',
        signedAt: c.signedAt,
        signed: c.status === 'signed',
      });
  // The list didn't come back but the summary did: the live plan is still linkable.
  if (live && !tp.some((p) => p.id === live))
    tp.unshift({
      id: live,
      label: `Treatment plan (${summary?.signed ? 'signed' : 'draft'})`,
      goals: goalsOf(live),
      problems: problemsOf(live),
      interventions: interventionsOf(live),
      diagnoses: diagnosesOf(live),
      source: 'tp',
      signed: Boolean(summary?.signed),
    });
  const seen = new Set<string>();
  const out: LinkablePlan[] = [];
  for (const p of [...tp, ...ehr]) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    out.push(p);
  }
  if (keepId && !seen.has(keepId))
    out.push({ id: keepId, label: 'Linked treatment plan', goals: [], source: 'tp' });
  return out;
}

/**
 * ehr#855: the plan a new note links by default: the newest signed plan, wherever it was written.
 * A draft (or a plan still waiting on a co-signature) is the default only when the client has no
 * signed plan at all; then `unsigned` is true so the note can say so.
 */
export function defaultLinkablePlan(
  choices: LinkablePlan[],
): { plan: LinkablePlan; unsigned: boolean } | null {
  const signed = choices.filter((p) => p.signed);
  if (signed.length) {
    // Newest signature first; on a tie (or no date) the list's own order wins.
    const at = (p: LinkablePlan) => String(p.signedAt ?? '');
    const plan = signed.reduce((best, p) => (at(p) > at(best) ? p : best));
    return { plan, unsigned: false };
  }
  return choices[0] ? { plan: choices[0], unsigned: true } : null;
}

/** A TP plan as a row of the chart's Treatment plans list. */
export interface TpListRow {
  plan: TpPlanCard;
  /** The status in the notes list's own pill vocabulary (NoteStatusPill). */
  status: 'draft' | 'pending' | 'changes' | 'signed';
  mode: PlanMode;
  where: 'Current' | 'On hold' | 'Past plan';
  /** Where the row opens in the TP app; null for an archived plan (the TP app can't open it). */
  url: string | null;
}

const TP_NOTE_STATUS: Record<TpPlanStatus, TpListRow['status']> = {
  draft: 'draft',
  submitted: 'pending',
  received: 'pending',
  signed: 'signed',
  revision: 'changes',
};

/**
 * Every TP plan on file for the list, the live one first. The live plan opens in the TP app on
 * what it needs next (as the old Treatment Plans tab did, tpPlanUrl); a plan on hold opens on
 * TP's resume link (tp#54); an archived plan is listed but has nowhere to open. tp#299: with no
 * TP link (an archived client's chart, tpChartLink) every row is listed and none opens.
 */
export function tpListRows(
  tpList: TpPlanList | null | undefined,
  tpLink: string | null,
  today: string,
): TpListRow[] {
  if (!tpList?.available) return [];
  const sep = tpLink?.includes('?') ? '&' : '?';
  return sortPlans(tpList.plans).map((plan) => {
    const where: TpListRow['where'] = plan.isCurrent
      ? 'Current'
      : plan.onHold
        ? 'On hold'
        : 'Past plan';
    const url = !tpLink
      ? null
      : plan.isCurrent
        ? tpPlanUrl(tpLink, describePlan(plan, computePlanFlags(plan, today)).action)
        : plan.onHold
          ? `${tpLink}${sep}resume=${encodeURIComponent(plan.id)}`
          : null;
    return {
      plan,
      status: TP_NOTE_STATUS[plan.status] ?? 'draft',
      mode: planMode(plan, tpList.plans),
      where,
      url,
    };
  });
}
